"use client";

import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
// Removed Select components - no longer selecting payment method
import { Users, DollarSign } from "lucide-react";
import { toast } from "sonner";
import { PricingService } from "@/lib/pricing/pricing-service-unified";
import { getCurrencySymbol } from "@/lib/utils/currency";
import { getCustomerType } from "@/lib/pricing/types";

interface MarkAsPaidModalProps {
  booking: {
    bookingId: number;
    bookingNumber: string;
    showName: string;
    customerName: string;
    participantCount: number;
    actualParticipantCount: number;
    baseAmountExclVat: number;
    vatAmount: number;
    /** Active add-ons (net and VAT); kept in booking_add_ons, not in the base/VAT columns. */
    addOnsExclVat?: number;
    addOnsVatAmount?: number;
    paymentPreference: string;
    studioName: string;
    startTime: string;
    endTime: string;
    customerTypeId: number;
    vatPercentage?: number;
  };
  venue: {
    id: number;
    name: string;
    currency: string;
  };
  onClose: () => void;
  onSuccess: () => void;
}

export default function MarkAsPaidModal({
  booking,
  venue,
  onClose,
  onSuccess,
}: MarkAsPaidModalProps) {
  const [actualParticipants, setActualParticipants] = useState<string>(
    booking.actualParticipantCount?.toString() ||
      booking.participantCount.toString(),
  );
  const [actualAmount, setActualAmount] = useState<string>("");
  // Removed paymentMethod state - mark as paid doesn't change payment method
  const [saving, setSaving] = useState(false);

  const customerType = getCustomerType(booking.customerTypeId);
  const isCorporate = customerType === "corporate";
  const vatRate = booking.vatPercentage || 25;

  // The amount to collect comes from the canonical base_amount_excl_vat /
  // vat_amount columns (the legacy total_price column is no longer maintained,
  // so it goes stale after a customer-side reprice) plus the active add-ons,
  // which are stored per line in booking_add_ons. Corporate customers are
  // quoted excl. VAT, private customers incl. VAT.
  const addOnsExclVat = booking.addOnsExclVat || 0;
  const addOnsVatAmount = booking.addOnsVatAmount || 0;
  const expectedDisplayAmount = isCorporate
    ? booking.baseAmountExclVat + addOnsExclVat
    : booking.baseAmountExclVat +
      booking.vatAmount +
      addOnsExclVat +
      addOnsVatAmount;
  const displayLabel = isCorporate ? "excl. VAT" : "incl. VAT";

  // Set initial amount if not set
  if (!actualAmount) {
    setActualAmount(expectedDisplayAmount.toFixed(2));
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const participants = parseInt(actualParticipants);
    const amount = parseFloat(actualAmount);

    // Client-side validation
    if (isNaN(participants) || participants < 0) {
      toast.error("Please enter a valid number of participants");
      return;
    }

    if (isNaN(amount) || amount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    try {
      setSaving(true);

      // Calculate pricing breakdown for the entered amount
      const finalPricing = PricingService.calculateVATComponents(
        amount,
        vatRate,
        customerType,
      );

      const payload = {
        actualParticipants: participants,
        manualOverride: {
          amount: amount,
          baseAmountExclVat: finalPricing.baseAmount,
          vatAmount: finalPricing.vatAmount,
          vatRate: vatRate,
          customerType: customerType,
        },
      };

      const response = await fetch(
        `/api/bookings/${booking.bookingId}/payment`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );

      if (response.ok) {
        toast.success("Payment recorded successfully!");
        onSuccess();
        onClose();

        // Reset form
        setActualParticipants(booking.participantCount.toString());
        setActualAmount(expectedDisplayAmount.toFixed(2));
      } else {
        const error = await response.json();
        toast.error(error.error || "Failed to mark as paid");
      }
    } catch (error) {
      console.error("Error marking as paid:", error);
      toast.error("Failed to mark as paid");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    onClose();
    // Reset form on close
    setActualParticipants(
      booking.actualParticipantCount?.toString() ||
        booking.participantCount.toString(),
    );
    setActualAmount(expectedDisplayAmount.toFixed(2));
  };

  return (
    <Dialog open={true} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="text-xl">Mark as paid</DialogTitle>
            <DialogDescription>
              Enter the actual number of participants and the total amount
              charged ({displayLabel}).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Actual Participants */}
            <div className="space-y-2">
              <Label
                htmlFor="actualParticipants"
                className="flex items-center gap-2"
              >
                <Users className="h-4 w-4 text-gray-500" />
                <span>Actual participants</span>
              </Label>
              <Input
                id="actualParticipants"
                type="number"
                min="0"
                value={actualParticipants}
                onChange={(e) => setActualParticipants(e.target.value)}
                placeholder="Number of participants"
                required
                className="text-lg"
              />
              <p className="text-sm text-muted-foreground">
                Booked: {booking.participantCount} participants
              </p>
            </div>

            {/* Actual Amount */}
            <div className="space-y-2">
              <Label htmlFor="actualAmount" className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-gray-500" />
                <span>Total amount ({displayLabel})</span>
              </Label>
              <div className="relative">
                <Input
                  id="actualAmount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={actualAmount}
                  onChange={(e) => setActualAmount(e.target.value)}
                  placeholder="0.00"
                  required
                  className="text-lg pr-16"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground font-medium">
                  {getCurrencySymbol(venue.currency)}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                Expected: {expectedDisplayAmount.toFixed(2)} {getCurrencySymbol(venue.currency)} •
                Total charged on the card machine
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Processing..." : "Confirm payment"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
